import React, { useEffect, useState, type JSX } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/authCore";
import {
  fetchUserProfile,
  upsertUserProfile,
} from "../../services/userService";
import { validateRollNumber } from "../../utils/rollValidator";

type Props = {
  onClose: () => void;
};

const ProfileModal = ({ onClose }: Props): JSX.Element => {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [loading, setLoading] = useState(false);
  const [rollNo, setRollNo] = useState<string>("");
  const [fullName, setFullName] = useState<string | null>(
    user?.displayName || null
  );
  const [points, setPoints] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!uid) return;
      setLoading(true);
      try {
        const profile = await fetchUserProfile(uid);
        if (!mounted) return;
        if (profile) {
          setRollNo(profile.rollNo || "");
          setFullName(profile.fullName ?? user?.displayName ?? null);
          setPoints(profile.points ?? null);
        } else {
          setRollNo("");
          setFullName(user?.displayName ?? null);
        }
      } catch {
        // noop
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [uid, user]);

  const handleSave = async () => {
    if (!uid) return setMessage("Not signed in.");
    const val = String(rollNo || "").trim();
    if (!val) {
      setMessage("Please enter your full roll number (e.g. GCECTB-R22-3001).");
      return;
    }
    const res = validateRollNumber(val);
    if (!res.valid) {
      setMessage(res.error ?? "Invalid roll number");
      return;
    }
    setLoading(true);
    try {
      await upsertUserProfile(uid, {
        rollNo: rollNo || null,
        fullName: fullName || null,
      });
      setMessage("Profile saved.");
    } catch {
      setMessage("Failed to save profile.");
    }
    setLoading(false);
  };

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 6000,
        padding: "1rem",
        backgroundColor: "rgba(0,0,0,0.6)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          // use relative positioning so the overlay's flexbox centers this element
          position: "relative",
          width: "min(520px, calc(100vw - 2rem))",
          background: "#050607",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          padding: "1.25rem clamp(1rem, 4vw, 1.5rem)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h4 style={{ margin: 0, color: "#fff" }}>Profile</h4>
        <p style={{ color: "#c7cbd1", marginTop: 8 }}>
          Manage your profile details used for CP contests.
        </p>

        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <label style={{ color: "#c7cbd1", fontSize: "0.85rem" }}>
            Full name
            <input
              value={fullName ?? ""}
              onChange={(e) => setFullName(e.target.value)}
              className="form-control mt-1"
            />
          </label>

          <label style={{ color: "#c7cbd1", fontSize: "0.85rem" }}>
            Roll number
            <input
              value={rollNo}
              onChange={(e) => setRollNo(e.target.value)}
              className="form-control mt-1"
              placeholder="Enter your college roll number"
            />
          </label>

          <div style={{ color: "#c7cbd1" }}>
            Points: <strong style={{ color: "#fff" }}>{points ?? 0}</strong>
          </div>

          {message && <div style={{ color: "#9fd" }}>{message}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-outline-light" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== "undefined" && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default ProfileModal;
